/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Typography, message, Space, Tag, Tooltip, Button } from 'antd';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';
import { PaperClipOutlined } from '@ant-design/icons';

import {
  getCorrectionLetterAttachmentDownloadUrl,
  getCorrectionLetterAttachmentDownloadUrlVendor,
  getInstantMessageAttachmentLocation,
  getInstantMessageAttachmentLocationVendor,
  getGlobalEvaluationInstantMessageAttachmentLocation,
  getGlobalEvaluationInstantMessageAttachmentLocationVendor,
} from '~src/services/api/movements';
import moment from 'moment';
import { MESSAGE_TYPE_CONFIG } from '~src/utils/messageTypeUtils';

export type InstantMessage = {
  message_id: number;
  id?: number;
  title: string;
  description: string;
  type: 2 | 5 | 6; // 2 = Correction Letter, 5 = Instant Message, 6 = Evaluation Instant Message
  reference?: string | null;
  sent_at?: number;
  created_by_full_name: string;
  created_at: number;
  attachments?: {
    file_guid: string;
    name: string;
  }[];
};

type Props = {
  projectId: number;
  tenderId: string;
  letters?: InstantMessage[];
  isLoading?: boolean;
  onViewLetter?: (letter: InstantMessage) => void;
  isOnTender?: boolean;
  shareId?: string;
  shareKey?: string;
};

const TenderCorrectionLettersList: React.FC<Props> = ({
  projectId,
  tenderId,
  letters,
  isLoading,
  onViewLetter,
  isOnTender,
  shareId,
  shareKey,
}) => {
  const intl = useIntl();

  const handleDownloadAttachment = async (
    letterId: number | undefined,
    fileGuid: string,
    type: 2 | 5 | 6
  ) => {
    if (!letterId) return;

    try {
      let url = '';

      if (isOnTender) {
        // Vendor side download logic
        if (!shareId || !shareKey) {
          message.error('Missing vendor credentials (shareId/shareKey)');
          return;
        }

        if (type === 2) {
          url = await getCorrectionLetterAttachmentDownloadUrlVendor({
            shareId,
            shareKey,
            tender_correction_letter_id: letterId,
            file_guid: fileGuid,
          });
        } else if (type === 5) {
          url = await getInstantMessageAttachmentLocationVendor({
            shareId,
            shareKey,
            instant_message_id: letterId,
            file_guid: fileGuid,
          });
        } else if (type === 6) {
          url = await getGlobalEvaluationInstantMessageAttachmentLocationVendor(
            {
              shareId,
              shareKey,
              instantMessageId: letterId,
              fileGuid,
            }
          );
        }
      } else {
        // Project side download logic
        if (type === 2) {
          url = await getCorrectionLetterAttachmentDownloadUrl({
            project_id: Number(projectId),
            tender_id: Number(tenderId),
            tender_correction_letter_id: letterId,
            file_guid: fileGuid,
          });
        } else if (type === 5) {
          url = await getInstantMessageAttachmentLocation({
            projectId: Number(projectId),
            tenderId: Number(tenderId),
            instantMessageId: letterId,
            fileGuid: fileGuid,
          });
        } else if (type === 6) {
          url = await getGlobalEvaluationInstantMessageAttachmentLocation({
            projectId: Number(projectId),
            tenderId: Number(tenderId),
            instantMessageId: letterId,
            fileGuid,
          });
        }
      }

      if (!url) {
        message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
        return;
      }

      window.open(url, '_blank');
    } catch (e) {
      console.error(e);
      message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
    }
  };

  if (isLoading) {
    return (
      <div
        css={{
          padding: 16,
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          background: '#fafafa',
        }}>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: 'loading' })}
        </Typography.Text>
      </div>
    );
  }

  if (!letters?.length) {
    return (
      <div
        css={{
          padding: 16,
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          background: '#fafafa',
        }}>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: 'no-data' })}
        </Typography.Text>
      </div>
    );
  }

  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {letters.map((l) => {
        const currentId = l.id || l.message_id;

        let refConfig = null;
        if (l.type === 5) {
          const refKey = l.reference || 'GEN';
          refConfig = MESSAGE_TYPE_CONFIG.find((c) => c.key === refKey);
        }

        return (
          <div
            key={l.message_id || l.id}
            css={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              background: '#fff',
              padding: '16px 16px 10px 16px',
            }}>
            <div css={{ display: 'block' }}>
              {/* RIGHT BLOCK: TAG, BTN, TYPE*/}
              <div
                css={{
                  float: 'right',
                  marginLeft: '24px',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}>
                <div
                  css={{ height: 32, display: 'flex', alignItems: 'center' }}>
                  <Tag
                    color={
                      l.type === 2 ? 'blue' : l.type === 6 ? 'purple' : 'green'
                    }
                    style={{ margin: 0 }}>
                    {l.type === 2
                      ? intl.formatMessage({ id: 'correction-letter' })
                      : l.type === 6
                      ? intl.formatMessage({ id: 'evaluation-instant-message' })
                      : intl.formatMessage({ id: 'instant-message' })}
                  </Tag>
                </div>

                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 4,
                  }}>
                  <Button type="primary" onClick={() => onViewLetter?.(l)}>
                    {intl.formatMessage({
                      id: 'preview',
                      defaultMessage: 'Preview',
                    })}
                  </Button>

                  {refConfig && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: refConfig.color,
                        fontWeight: 500,
                        fontSize: '12px',
                      }}>
                      <span style={{ display: 'flex' }}>{refConfig.icon}</span>
                      <span>
                        {intl.formatMessage({ id: refConfig.labelId })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* LEFT: Title & Description */}
              <Typography.Title
                level={5}
                css={{
                  marginTop: '5px',
                  marginBottom: 0,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}>
                {l.title || '-'}
              </Typography.Title>

              {!!l.description && (
                <Typography.Paragraph
                  css={{
                    marginTop: 8,
                    marginBottom: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}>
                  {l.description}
                </Typography.Paragraph>
              )}

              <div css={{ clear: 'both' }} />
            </div>

            <div css={{ marginTop: 12 }}>
              <Typography.Text strong>
                {intl.formatMessage({ id: 'correction-letter-attachments' })}
              </Typography.Text>

              {l.attachments?.length ? (
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    marginTop: 8,
                  }}>
                  {l.attachments.map((a) => {
                    const isPdf = a.name?.toLowerCase()?.includes('.pdf');

                    return (
                      <div
                        key={a.file_guid}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                        }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 0,
                            flex: 1,
                          }}>
                          {isPdf ? (
                            <IconButton
                              icon={'file-pdf'}
                              iconStyle={'fad'}
                              color={'red'}
                              backgroundOff
                              customStyle={{ fontSize: '18px' }}
                              containerStyle={{
                                maxHeight: '18px',
                                maxWidth: '18px',
                                minHeight: '18px',
                                minWidth: '18px',
                              }}
                            />
                          ) : (
                            // @ts-ignore
                            <PaperClipOutlined style={{ fontSize: '18px' }} />
                          )}

                          <Typography.Text
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                            {a.name}
                          </Typography.Text>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {/* <IconButton
                            icon={'download'}
                            iconStyle={'far'}
                            color={'blue'}
                            click={() => {
                              handleDownloadAttachment(
                                currentId,
                                a.file_guid,
                                l.type
                              );
                            }}
                            customStyle={{
                              fontSize: '16px',
                              cursor: 'pointer',
                            }}
                          />
                          */}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Typography.Text
                  type="secondary"
                  css={{ display: 'block', marginTop: 8 }}>
                  {intl.formatMessage({
                    id: 'correction-letter-attachments-not-implemented',
                  })}
                </Typography.Text>
              )}

              {/* Display Created By and Created At */}
              <Typography.Text
                type="secondary"
                style={{
                  fontSize: '12px',
                  display: 'block',
                  width: '100%',
                  textAlign: 'right',
                }}>
                {l.created_by_full_name} •{' '}
                {moment.unix(l?.sent_at).format('DD-MM-YYYY, HH:mm')}
              </Typography.Text>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TenderCorrectionLettersList;
